// FloatingDisclaimer.tsx

import React, { useState, useEffect } from "react";
import { Paper, Typography, Box, Button, Slide, Link } from "@mui/material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { useTranslation, Trans } from "react-i18next";

interface FloatingDisclaimerProps {
  onAccept?: () => void;
}

export const FloatingDisclaimer: React.FC<FloatingDisclaimerProps> = ({ onAccept }) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);

  useEffect(() => {
    // Check if user has already accepted this disclaimer in the past
    const hasAccepted = localStorage.getItem("tnc_accepted");
    if (!hasAccepted) {
      // Small delay for better UX entrance animations
      const timer = setTimeout(() => setOpen(true), 1000);
      return () => clearTimeout(timer);
    }
  }, []);

  const handleAccept = () => {
    localStorage.setItem("tnc_accepted", "true");
    setOpen(false);
    if (onAccept) onAccept();
  };

  return (
    <Slide direction="up" in={open} mountOnEnter unmountOnExit>
      <Paper
        elevation={6}
        sx={{
          position: "fixed",
          bottom: 24,
          left: "50%",
          transform: "translateX(-50%) !important",
          width: "90%",
          maxWidth: 760,
          zIndex: 2000,
          p: { xs: 2, sm: 2.5 },
          borderRadius: 2,
          border: "1px solid",
          borderColor: "divider",
          bgcolor: "background.paper",
          display: "flex",
          flexDirection: { xs: "column", sm: "row" },
          alignItems: { xs: "stretch", sm: "center" },
          gap: 2,
        }}
      >
        {/* Info Icon & Core Text */}
        <Box sx={{ display: "flex", gap: 1.5, alignItems: "flex-start", flex: 1 }}>
          <InfoOutlinedIcon sx={{ mt: 0.25, color: "primary.main" }} />
          <Box>
            <Typography variant="subtitle2" sx={{ fontWeight: "bold", mb: 1, color: "text.primary" }}>
              {t("components.floatingDisclaimer.title")}
            </Typography>
            <Typography variant="body2" sx={{ color: "text.secondary", lineHeight: 1.5 }}>
              <Trans
                i18nKey="components.floatingDisclaimer.body"
                components={{
                  link: <Link href="/en/tnc" underline="hover" sx={{ fontWeight: 500, color: "primary.main" }} />,
                }}
              />
            </Typography>
          </Box>
        </Box>

        {/* Call to Action Button */}
        <Box sx={{ display: "flex", justifyContent: "flex-end", minWidth: { sm: 110 } }}>
          <Button
            variant="contained"
            color="primary"
            size="medium"
            onClick={handleAccept}
            sx={{
              px: 3,
              width: { xs: "100%", sm: "auto" },
            }}
          >
            {t("components.floatingDisclaimer.acceptButton")}
          </Button>
        </Box>
      </Paper>
    </Slide>
  );
};
